// What a day allows, and what the moment calls for. Pure, so the drag, the route chips and the
// one action button all agree, and the tests can hold them to Kyle's rules.
import { fuelFor } from "./purpose.ts";
import type { PlanDay, Route, State, Feel } from "./types";
import { AXIS_START, AXIS_END, darkMinutes } from "./band.ts";
import { toMin, hhmm, sunTimes } from "./sun.ts";
import { movingMin, STOPS_MIN, unlitAfterSunrise, fitsLight } from "./ride.ts";
import { dow } from "./dates.ts";
import { homeOf } from "../data/empty.ts";

export type Wall = { start: number; end: number; label: string };
export const SNAP = 5;
const roundUp = (m: number, step = 15) => Math.ceil(m / step) * step;
export const snap = (m: number) => Math.round(m / SNAP) * SNAP;

const isWeekday = (date: string) => { const d = dow(date); return d >= 1 && d <= 5; };

/** Kyle, 26 October: Thursdays from home, 09:00 to 17:00; other weekdays 07:00 to 18:00 with the commute. */
export const WORK = { thursday: ["09:00", "17:00"] as [string, string], weekday: ["07:00", "18:00"] as [string, string] };

/**
 * The walls a ride on this day cannot cross. Thursday: from the morning finish (08:45, so he is
 * at his desk for 09:00) to 17:00. Other weekdays: 07:00 to 18:00, door to door. Every day:
 * finished by the latest finish (21:30). Kept windows on the day come from the plan.
 */
export function wallsFor(s: State, date: string, kept: Wall[] = []): Wall[] {
  const w: Wall[] = [...kept];
  const hours = s.settings.workHours ?? WORK;
  if (dow(date) === 4) w.push({ start: Math.min(toMin(s.settings.morningFinishBy ?? "08:45"), toMin(hours.thursday[0])), end: toMin(hours.thursday[1]), label: "Work from home" });
  else if (isWeekday(date)) w.push({ start: toMin(hours.weekday[0]), end: toMin(hours.weekday[1]), label: "Work and the commute" });
  w.push({ start: toMin(s.settings.latestFinish ?? "21:30"), end: AXIS_END, label: `Done by ${s.settings.latestFinish ?? "21:30"}` });
  return w.sort((a, b) => a.start - b.start);
}

/** Starts a ride of `dur` minutes can take without crossing a wall, as closed intervals. */
export function validStarts(dur: number, walls: Wall[]): [number, number][] {
  let free: [number, number][] = [[AXIS_START, AXIS_END - dur]];
  for (const w of walls) {
    const lo = w.start - dur, hi = w.end; // starting strictly inside (lo, hi) overlaps the wall
    free = free.flatMap(([a, b]) => {
      const out: [number, number][] = [];
      if (a <= lo) out.push([a, Math.min(b, lo)]);
      if (b >= hi) out.push([Math.max(a, hi), b]);
      return out;
    });
  }
  return free.filter(([a, b]) => b >= a);
}

/** The nearest valid start to `want`, snapped. `hit` names the wall it stopped against, if any. */
export function clampStart(want: number, dur: number, walls: Wall[]): { start: number; hit: Wall | null } {
  const free = validStarts(dur, walls);
  if (!free.length) return { start: snap(want), hit: walls[0] ?? null };
  let best = free[0][0], dist = Infinity;
  for (const [a, b] of free) {
    const c = Math.min(b, Math.max(a, want));
    if (Math.abs(c - want) < dist) { dist = Math.abs(c - want); best = c; }
  }
  // Snap inside the interval it landed in.
  const [a, b] = free.find(([x, y]) => best >= x && best <= y)!;
  let start = snap(best);
  if (start < a) start += SNAP;
  if (start > b) start -= SNAP;
  if (dist < 0.5) return { start, hit: null };
  const end = start + dur;
  const hit = walls.find((w) => Math.abs(w.start - end) <= SNAP || Math.abs(w.end - start) <= SNAP) ?? (want < AXIS_START ? null : walls[walls.length - 1]);
  return { start, hit: hit ?? null };
}

export type RouteOption = { id: string; name: string; km: number | null; gain: number | null; start: number; end: number; fits: boolean; why?: string };

/**
 * Routes for this day at about this time, each placed where it fits. The city loop takes the
 * time there is; a library route takes its modelled time plus stops, rounded to the quarter hour.
 * A route whose unlit lanes would fall in the dark, or that cannot fit the walls, is not offered.
 */
export function routeOptions(s: State, date: string, start: number, end: number, walls: Wall[]): RouteOption[] {
  const { lat, lon } = homeOf(s);
  const out: RouteOption[] = [{ id: "city-loop", name: "City loop, lit", km: null, gain: null, start, end, fits: true }];
  for (const r of s.routes) {
    const dur = roundUp(movingMin(r.km, r.gain) + STOPS_MIN);
    const { start: st } = clampStart(start, dur, walls);
    const free = validStarts(dur, walls).some(([a, b]) => st >= a && st <= b);
    const opt: RouteOption = { id: r.id, name: r.n, km: r.km, gain: r.gain, start: st, end: st + dur, fits: free };
    if (!free) { opt.why = "Too long for the time there is"; }
    else if (st < 12 * 60 && !unlitAfterSunrise(r, date, hhmm(st), lat, lon)) { opt.fits = false; opt.why = "Its lanes would be dark"; }
    else if (st >= 12 * 60 && !fitsLight(r, date, hhmm(st), lat, lon, s.settings.latestFinish, s.settings.bufferMin, s.settings.sunsetRule !== false)) { opt.fits = false; opt.why = "Its lanes would be dark"; }
    out.push(opt);
  }
  return out;
}

/** Kit items from the kit line, then the lights if any of it is dark, then the Roam. */
export function readyItems(kit: string | null | undefined, darkMin: number, fuel: string[] = ["Bottle"]): string[] {
  const words = (kit ?? "").replace(/\s*\(.*\)\s*$/, "").split(/,\s*|\s+and\s+/).map((x) => x.trim().replace(/^an? /i, "")).filter(Boolean);
  const items = words.map((w) => w[0].toUpperCase() + w.slice(1));
  if (darkMin > 0) items.push("Front light", "Rear light");
  items.push("Roam charged", ...fuel);
  return items;
}

/** Minutes after midnight, London time. */
export function londonNow(now = new Date()): { date: string; min: number } {
  const f = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(now).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, min: Number(p.hour) * 60 + Number(p.minute) };
}

export type Moment =
  | { kind: "debrief"; date: string }                    // a ride landed and has no feel yet
  | { kind: "go"; date: string }                         // the ride is today and not over
  | { kind: "missed"; date: string }                     // today's ride time has passed with nothing on Strava
  | { kind: "book"; date: string }                       // the next ride is proposed or chosen
  | { kind: "lay-out"; date: string; left: number }      // booked, the night before, kit not all laid out
  | { kind: "ready"; date: string }                      // booked and nothing to do yet
  | { kind: "none" };                                    // nothing ahead this week

/** The one thing the moment calls for. First match wins. */
export function momentFor(s: State, now = new Date()): Moment {
  const { date: today, min } = londonNow(now);
  const days = s.plan.flatMap((w) => w.days);
  const feel = s.log?.feel ?? {};
  const rode = (d: string) => s.rides.some((r) => r.d === d && r.km >= 15);

  // A ride in the last two days with no feel yet.
  const recent = s.rides.filter((r) => r.km >= 15 && r.d <= today && r.d >= shift(today, -2)).sort((a, b) => (a.d < b.d ? 1 : -1))[0];
  if (recent && !feel[recent.d]) return { kind: "debrief", date: recent.d };

  const live = days.filter((d) => d.type === "ride" && d.status !== "cancelled" && d.start && d.end).sort((a, b) => (a.date < b.date ? -1 : 1));
  const todays = live.find((d) => d.date === today);
  if (todays && !rode(today)) {
    if (min < toMin(todays.end!)) return todays.status === "booked" ? { kind: "go", date: today } : { kind: "book", date: today };
    return { kind: "missed", date: today };
  }
  const next = live.find((d) => d.date > today && d.status !== "done");
  if (!next) return { kind: "none" };
  if (next.status !== "booked") return { kind: "book", date: next.date };
  const eve = next.date === shift(today, 1) && min >= 17 * 60;
  const left = (readyCount(s, next) - (s.log?.ready?.[next.date]?.length ?? 0));
  if (eve && left > 0) return { kind: "lay-out", date: next.date, left };
  return { kind: "ready", date: next.date };
}

function readyCount(s: State, d: PlanDay): number {
  const { rise, set } = sunTimes(homeOf(s).lat, homeOf(s).lon, d.date);
  const dark = d.start && d.end ? darkMinutes({ start: toMin(d.start), end: toMin(d.end) }, rise, set) : 0;
  return readyItems(d.kit ?? "jacket, long sleeve top and bib tights", dark, fuelFor(d).items).length;
}

const shift = (iso: string, n: number) => { const [y, m, d] = iso.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10); };

export const FEELS: { id: Feel; label: string }[] = [
  { id: "easy", label: "Easy" }, { id: "steady", label: "Steady" }, { id: "hard", label: "Hard" }, { id: "cooked", label: "Cooked" },
];

export type { Route };
